import { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Calendar, DollarSign, CheckCircle, XCircle, Clock, Pause, Play, SkipForward, AlertCircle } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';

export const Subscriptions: React.FC = () => {
  const { dashboardData, isLoading } = useAuth();
  const [actionLoading, setActionLoading] = useState<{ [key: string]: string | null }>({});

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading your subscriptions...</p>
        </div>
      </div>
    );
  }

  const subscriptions = dashboardData?.subscriptions || [];
  const activeSubscriptions = subscriptions.filter(s => s.status === 'active');
  const totalSubscriptions = subscriptions.length;

  // Calculate total MRR from active subscriptions
  const totalMRR = activeSubscriptions.reduce((sum, sub) => {
    // Convert frequency to monthly value
    const monthlyValue = sub.frequency === '30 days' ? sub.price :
                        sub.frequency === '60 days' ? sub.price / 2 :
                        sub.frequency === '90 days' ? sub.price / 3 : sub.price;
    return sum + monthlyValue;
  }, 0);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-success-50 text-success-700">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        );
      case 'paused':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-warning-50 text-warning-700">
            <Pause className="w-3 h-3" />
            Paused
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-error-50 text-error-700">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">
            <Clock className="w-3 h-3" />
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSubscriptionAction = async (subscriptionId: string, action: 'pause' | 'resume' | 'skip' | 'cancel') => {
    // Set loading state for this specific action
    setActionLoading(prev => ({ ...prev, [subscriptionId]: action }));

    try {
      // TODO: Implement API calls when backend endpoints are ready
      console.log(`${action.toUpperCase()} subscription:`, subscriptionId);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Show success feedback
      alert(`Subscription ${action} successful! (Backend endpoint not yet implemented)`);
    } catch (error) {
      console.error(`Failed to ${action} subscription:`, error);
      alert(`Failed to ${action} subscription. Please try again.`);
    } finally {
      // Clear loading state
      setActionLoading(prev => ({ ...prev, [subscriptionId]: null }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-neutral-900 mb-2">
          Subscriptions
        </h1>
        <p className="text-neutral-600">
          Manage your active subscriptions and delivery schedule
        </p>
      </div>

      {/* Summary Stats */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <Package className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Total Subscriptions</p>
                  <p className="text-2xl font-bold text-neutral-900">{totalSubscriptions}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-success-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-success-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Active</p>
                  <p className="text-2xl font-bold text-neutral-900">{activeSubscriptions.length}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <DollarSign className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Monthly Value</p>
                  <p className="text-2xl font-bold text-neutral-900">{formatCurrency(totalMRR)}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </motion.div>

      {/* Info Banner */}
      {activeSubscriptions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card variant="glass" className="bg-primary-50 border-primary-200">
            <CardBody className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-neutral-700">
                  <p className="font-medium text-neutral-900 mb-1">Subscription Management</p>
                  <p>You can pause, skip, or cancel your subscriptions at any time. Changes will take effect on your next billing date.</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      )}

      {/* Subscriptions List */}
      <div className="space-y-4">
        {subscriptions.length === 0 ? (
          <Card>
            <CardBody className="p-12 text-center">
              <Package className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                No Subscriptions Yet
              </h3>
              <p className="text-neutral-600 mb-6">
                Start a subscription to get regular deliveries at a discounted price.
              </p>
              <Button variant="primary" gradient>
                Browse Products
              </Button>
            </CardBody>
          </Card>
        ) : (
          subscriptions.map((subscription) => {
            const isActive = subscription.status === 'active';
            const isPaused = subscription.status === 'paused';
            const isCancelled = subscription.status === 'cancelled';
            const currentAction = actionLoading[subscription.id];

            return (
              <motion.div
                key={subscription.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="hover:shadow-lg transition-shadow">
                  <CardBody className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                      {/* Subscription Info */}
                      <div className="flex-1">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="p-3 bg-neutral-50 rounded-lg">
                            <Package className="w-6 h-6 text-neutral-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-neutral-900">
                                Subscription #{subscription.id.substring(0, 8)}
                              </h3>
                              {getStatusBadge(subscription.status)}
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-neutral-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Every {subscription.frequency}
                              </span>
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {formatCurrency(subscription.price)} per delivery
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Next Order Info */}
                        {isActive && (
                          <div className="bg-gradient-cool rounded-lg p-4">
                            <p className="text-sm text-neutral-600 mb-2">Next Delivery</p>
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Calendar className="w-5 h-5 text-primary-600" />
                              </div>
                              <div>
                                <p className="text-lg font-bold text-neutral-900">
                                  {formatDate(subscription.nextOrderDate)}
                                </p>
                                <p className="text-xs text-neutral-600">
                                  {Math.ceil((new Date(subscription.nextOrderDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days away
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {isCancelled && subscription.cancelledAt && (
                          <div className="bg-error-50 rounded-lg p-4">
                            <p className="text-sm text-error-900 font-medium mb-1">Subscription Cancelled</p>
                            <p className="text-sm text-error-700">
                              Cancelled on {formatDate(subscription.cancelledAt)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        {isActive && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              disabled={currentAction !== null}
                              onClick={() => handleSubscriptionAction(subscription.id, 'pause')}
                            >
                              {currentAction === 'pause' ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2" />
                                  Pausing...
                                </>
                              ) : (
                                <>
                                  <Pause className="w-4 h-4 mr-2" />
                                  Pause Subscription
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              disabled={currentAction !== null}
                              onClick={() => handleSubscriptionAction(subscription.id, 'skip')}
                            >
                              {currentAction === 'skip' ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2" />
                                  Skipping...
                                </>
                              ) : (
                                <>
                                  <SkipForward className="w-4 h-4 mr-2" />
                                  Skip Next Order
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              className="text-error-600 hover:bg-error-50 hover:border-error-200"
                              disabled={currentAction !== null}
                              onClick={() => {
                                if (confirm('Are you sure you want to cancel this subscription? This action cannot be undone.')) {
                                  handleSubscriptionAction(subscription.id, 'cancel');
                                }
                              }}
                            >
                              {currentAction === 'cancel' ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-error-600 mr-2" />
                                  Cancelling...
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel Subscription
                                </>
                              )}
                            </Button>
                          </>
                        )}

                        {isPaused && (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              fullWidth
                              gradient
                              disabled={currentAction !== null}
                              onClick={() => handleSubscriptionAction(subscription.id, 'resume')}
                            >
                              {currentAction === 'resume' ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                  Resuming...
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-2" />
                                  Resume Subscription
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              className="text-error-600 hover:bg-error-50 hover:border-error-200"
                              disabled={currentAction !== null}
                              onClick={() => {
                                if (confirm('Are you sure you want to cancel this subscription? This action cannot be undone.')) {
                                  handleSubscriptionAction(subscription.id, 'cancel');
                                }
                              }}
                            >
                              {currentAction === 'cancel' ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-error-600 mr-2" />
                                  Cancelling...
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel Subscription
                                </>
                              )}
                            </Button>
                          </>
                        )}

                        {isCancelled && (
                          <div className="text-sm text-neutral-500 italic text-center py-2">
                            No actions available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional Details */}
                    <div className="mt-6 pt-6 border-t border-neutral-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-neutral-500 mb-1">Started</p>
                          <p className="font-medium text-neutral-900">{formatDate(subscription.createdAt)}</p>
                        </div>
                        <div>
                          <p className="text-neutral-500 mb-1">Frequency</p>
                          <p className="font-medium text-neutral-900">{subscription.frequency}</p>
                        </div>
                        <div>
                          <p className="text-neutral-500 mb-1">Price</p>
                          <p className="font-medium text-neutral-900">{formatCurrency(subscription.price)}</p>
                        </div>
                        <div>
                          <p className="text-neutral-500 mb-1">Status</p>
                          <p className="font-medium text-neutral-900 capitalize">{subscription.status}</p>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Subscriptions;
